import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { finalize, map, switchMap, tap } from 'rxjs/operators';

import { AuthenticationMethod, GTMEventType } from '@app/models';
import { ErrorHandlerService, RegistrationContext, LoginContext, DateService, UserService, OrganizationService, GoogleAnalyticsService, AppService } from '@app/services';
import { SelectOption } from '@app/shared';
import { I18nService } from '@app/core';
import { of } from 'rxjs';

@Component({
  selector: 'app-auth-register',
  templateUrl: './auth-onboarding.component.html',
  styleUrls: ['../auth.scss', './auth-onboarding.component.scss'],
  standalone: false
})
export class AuthOnboardingComponent implements OnInit {
  form!: UntypedFormGroup;
  isLoading = false;
  isCompany = false;
  oauthForm!: UntypedFormGroup;

  readonly AuthenticationMethod = AuthenticationMethod;
  readonly countries: SelectOption[] = [];
  readonly languages: SelectOption[] = [];

  private navigationState: { [k: string]: any };

  constructor(
    public route: ActivatedRoute,
    public i18nService: I18nService,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private appService: AppService,
    private errorHandlerService: ErrorHandlerService,
    private dateService: DateService,
    private userService: UserService,
    private organizationService: OrganizationService,
    private googleAnalyticsService: GoogleAnalyticsService
  ) {
    this.oauthForm = this.formBuilder.group({ email: this.route.snapshot.queryParams.email || '', password: '', referral: this.route.snapshot.queryParams.r } as LoginContext);
    this.form = this.formBuilder.group({
      name: '',
      birthday: '',
      password: '',
      company: null,
      cnpj: null,
      referral: this.route.snapshot.queryParams.r || null,
      country: this.i18nService.navigatorCountry(),
      language: this.i18nService.language || null,
      timezone: this.i18nService.timezone || null
    } as RegistrationContext);
    this.navigationState = router.getCurrentNavigation()?.extras.state || {};

    this.languages = this.i18nService.supportedLanguagesAsSelectOptions || null;
    this.countries = this.i18nService.countriesWithCodes || null;
  }

  ngOnInit() {
    if (!this.navigationState?.fromAuthRegister) {
      this.router.navigate(['/entrar']);
    }
  }

  update() {
    this.isLoading = true;
    this.form.markAllAsTouched();
    this.form.value.birthday = this.dateService.normalizeDate(this.form.value.birthday);
    const params = this.form.value;
    const organization = params.company ? { name: params.company, cnpj: params.cnpj, country: params.country } : null;

    if (params.country !== 'BR' && organization?.cnpj) {
      delete organization.cnpj;
    }

    let isInvite = false;
    this.userService
      .currentUserOrganization()
      .pipe(
        map(org => (isInvite = Date.now() - new Date(org.created_at).getTime() > 60 * 1000)),
        switchMap(() => this.userService.updateCurrentUser({ user: { name: params.name, ...(organization ? { organization } : {}) } })),
        switchMap(() => this.userService.updateCurrentUserLocale({ locale: { country: params.country, language: params.language } })),
        tap(user => this.googleAnalyticsService.emitGTMEvent(GTMEventType.SignUpCompleted, user.id, { signup_origin: isInvite ? 'member' : 'homepage' })),
        switchMap(() => (organization ? this.organizationService.update({ organization }) : of(null))),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: () => {
          const redirectUrl = this.navigationState?.redirectUrl;
          this.appService.navigateAfterAuth(redirectUrl || '/', this.router);
        },
        error: error => this.errorHandlerService.handleValidation(this.form, error)
      });
  }

  changeLanguage(language: string) {
    this.i18nService.language = language;
    (this.languages as SelectOption[]) = this.i18nService.supportedLanguagesAsSelectOptions || null;
    (this.countries as SelectOption[]) = this.i18nService.countriesWithCodes || null;
    if (this.form.get('language')?.value) this.form.get('language').setValue(this.form.get('language').value);
    if (this.form.get('country')?.value) this.form.get('country').setValue(this.form.get('country').value);
  }
}
